/**
 * AnticipationEngine — re-scores Home widgets based on context signals so
 * the user sees the right thing at the right time without configuring
 * anything. Implements the "no-typing, one-glance, one-tap" doctrine.
 *
 * Signals consumed:
 *   - Time of day (Fajr/morning/noon/asr/evening/night windows)
 *   - Day of week (Friday → Jumu'ah prep boost)
 *   - Hijri month (Ramadan/Dhul-Hijjah/Muharram boosts)
 *   - Season + hemisphere (winter/summer adapts food/health)
 *   - Location delta (new country → currency/phrasebook auto-pin)
 *   - Weather state (bad AQI / extreme temp → AirCard pinned)
 *
 * Output: a stable score per widget id. WidgetEngine sorts by
 * `(manualPin ? Infinity : 0) + anticipationScore + defaultOrder * -0.01`
 * — manual pins always win, then anticipation, then declared order is
 * only a tiebreaker.
 *
 * Design rule: NEVER move a widget the user explicitly pinned. NEVER
 * surface a hidden widget without consent. We only reorder VISIBLE,
 * non-pinned widgets.
 */

import { gregorianToHijri } from '../../services/HijriDate';
import type { LocationState } from './types';

export interface AnticipationContext {
  now: Date;
  location: LocationState;
  /** Last seen country code, persisted so we can detect "country changed". */
  previousCountryCode: string | null;
  /** Last seen city name. Used to spot "user moved to a new city". */
  previousCity: string | null;
  /** Cached weather data — used for AirCard / extreme weather signals. */
  weatherTemp?: number | null;
  /** Air-quality index from the weather service (when present). */
  weatherAqi?: number | null;
}

export interface AnticipationScore {
  widgetId: string;
  score: number;
  reason: string;
}

/**
 * Time bucket of the day, with prayer-aware boundaries (rough — uses
 * solar noon = 12:00 as a heuristic; precise prayer-time buckets are
 * for the Prayer module, not the home anticipation).
 */
type DayPart = 'fajr' | 'morning' | 'midday' | 'afternoon' | 'evening' | 'night' | 'lateNight';

function getDayPart(d: Date): DayPart {
  const h = d.getHours();
  if (h >= 4 && h < 7) return 'fajr';
  if (h >= 7 && h < 11) return 'morning';
  if (h >= 11 && h < 14) return 'midday';
  if (h >= 14 && h < 17) return 'afternoon';
  if (h >= 17 && h < 20) return 'evening';
  if (h >= 20 && h < 23) return 'night';
  return 'lateNight';
}

type Season = 'winter' | 'spring' | 'summer' | 'autumn';

function getSeason(d: Date, lat: number | null): Season {
  const month = d.getMonth();
  const north = lat === null || lat >= 0;
  if (north) {
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'autumn';
    return 'winter';
  }
  if (month >= 2 && month <= 4) return 'autumn';
  if (month >= 5 && month <= 7) return 'winter';
  if (month >= 8 && month <= 10) return 'spring';
  return 'summer';
}

interface HijriContext {
  month: number; // 1-12
  day: number;   // 1-30
  isRamadan: boolean;
  isDhulHijjah: boolean;
  isFirst10DhulHijjah: boolean;
  isMuharram: boolean;
  isFirst10Muharram: boolean;
}

function getHijri(d: Date): HijriContext | null {
  try {
    const h = gregorianToHijri(d);
    return {
      month: h.month,
      day: h.day,
      isRamadan: h.month === 9,
      isDhulHijjah: h.month === 12,
      isFirst10DhulHijjah: h.month === 12 && h.day <= 10,
      isMuharram: h.month === 1,
      isFirst10Muharram: h.month === 1 && h.day <= 10,
    };
  } catch {
    return null;
  }
}

const STORAGE_PREV_COUNTRY = 'aweni_prev_country_code';
const STORAGE_PREV_CITY = 'aweni_prev_city';

export class AnticipationEngine {
  private scores: Map<string, AnticipationScore> = new Map();

  /** Build the context bundle from environment + persisted state. */
  static buildContext(
    location: LocationState,
    weatherTemp?: number | null,
    weatherAqi?: number | null,
  ): AnticipationContext {
    let prevCountry: string | null = null;
    let prevCity: string | null = null;
    try {
      prevCountry = localStorage.getItem(STORAGE_PREV_COUNTRY);
      prevCity = localStorage.getItem(STORAGE_PREV_CITY);
    } catch { /* ignore */ }
    return {
      now: new Date(),
      location,
      previousCountryCode: prevCountry,
      previousCity: prevCity,
      weatherTemp: weatherTemp ?? null,
      weatherAqi: weatherAqi ?? null,
    };
  }

  /** Persist the country/city seen on this run so the next run can detect a delta. */
  static commitObservedLocation(location: LocationState): void {
    try {
      if (location.countryCode) {
        localStorage.setItem(STORAGE_PREV_COUNTRY, location.countryCode);
      }
      if (location.city) {
        localStorage.setItem(STORAGE_PREV_CITY, location.city);
      }
    } catch { /* ignore */ }
  }

  /**
   * Compute the score for a given widget id under the current context.
   * Score range: roughly -10 (bury) to +50 (force to top); 0 = neutral.
   */
  computeScore(widgetId: string, ctx: AnticipationContext): AnticipationScore {
    const hijri = getHijri(ctx.now);
    const dayPart = getDayPart(ctx.now);
    const season = getSeason(ctx.now, ctx.location.lat);
    const isFriday = ctx.now.getDay() === 5;
    const isWeekend = ctx.now.getDay() === 0 || ctx.now.getDay() === 6;

    let score = 0;
    const reasons: string[] = [];

    // Country/city delta — the strongest "react to user moving" signal.
    const countryChanged = !!(
      ctx.previousCountryCode &&
      ctx.location.countryCode &&
      ctx.previousCountryCode !== ctx.location.countryCode
    );
    const cityChanged = !!(
      ctx.previousCity &&
      ctx.location.city &&
      ctx.previousCity !== ctx.location.city
    );

    // Per-widget rules. Order = priority of evaluation only; the score
    // accumulates so multiple signals can stack.
    switch (widgetId) {
      // -- Time-of-day boosts ---------------------------------------------
      case 'daily-verse':
      case 'wisdom-card':
        if (dayPart === 'fajr' || dayPart === 'morning') {
          score += 20; reasons.push('morning spiritual touch');
        }
        if (dayPart === 'lateNight') {
          score += 10; reasons.push('quiet hours reflection');
        }
        break;

      case 'news':
        if (dayPart === 'fajr' || dayPart === 'morning') {
          score += 15; reasons.push('morning briefing');
        }
        if (dayPart === 'evening') {
          score += 8; reasons.push('evening recap');
        }
        break;

      case 'memoriz-progress':
      case 'reading-progress':
        if (dayPart === 'fajr' || dayPart === 'lateNight' || dayPart === 'night') {
          score += 12; reasons.push('quiet reading hours');
        }
        break;

      case 'money-snapshot':
        if (dayPart === 'morning' || dayPart === 'midday') {
          score += 10; reasons.push('budget check during active hours');
        }
        if (ctx.now.getDay() === 1) {
          score += 5; reasons.push('Monday financial reset');
        }
        break;

      case 'recent-orders':
      case 'seasonal-food':
        if (dayPart === 'midday' || (dayPart === 'evening' && ctx.now.getHours() < 19)) {
          score += 14; reasons.push('meal hours');
        }
        if (isWeekend) score += 4;
        break;

      case 'audio-room':
        if (dayPart === 'morning' || dayPart === 'evening') {
          score += 10; reasons.push('listening hours');
        }
        if (hijri?.isRamadan && (dayPart === 'fajr' || dayPart === 'lateNight')) {
          score += 18; reasons.push('Ramadan recitation hours');
        }
        break;

      case 'on-this-day':
      case 'wisdom-card_secondary':
        if (dayPart === 'morning' || dayPart === 'midday') {
          score += 6; reasons.push('curiosity window');
        }
        break;

      case 'around-me':
        if (dayPart === 'midday' || dayPart === 'afternoon' || dayPart === 'evening') {
          score += 10; reasons.push('out-and-about hours');
        }
        if (ctx.location.traveling) { score += 25; reasons.push('travel mode'); }
        break;

      case 'kids-radar':
        if (dayPart === 'afternoon' || dayPart === 'evening') {
          score += 14; reasons.push('after-school window');
        }
        if (isWeekend) score += 8;
        break;

      // -- Friday Jumu'ah boost -------------------------------------------
      case 'jumuah-prep':
        if (isFriday) {
          if (dayPart === 'fajr' || dayPart === 'morning' || dayPart === 'midday') {
            score += 50; reasons.push('Jumu\'ah preparation hours');
          } else {
            score += 25; reasons.push('Friday context');
          }
        }
        break;

      // -- Hijri-month boosts ---------------------------------------------
      case 'ramadan-dashboard':
        if (hijri?.isRamadan) {
          score += 50; reasons.push('Ramadan');
          if (dayPart === 'evening') { score += 12; reasons.push('approaching iftar'); }
          if (dayPart === 'lateNight' || dayPart === 'fajr') { score += 10; reasons.push('approaching suhoor'); }
          if (dayPart === 'night') { score += 8; reasons.push('tarāwīḥ window'); }
        }
        break;

      case 'dhul-hijjah-dashboard':
        if (hijri?.isFirst10DhulHijjah) {
          score += 45; reasons.push('first 10 Dhul Hijjah');
          if (hijri.day === 9) { score += 10; reasons.push('Day of Arafah'); }
          if (hijri.day === 10) { score += 8; reasons.push('Eid al-Adha'); }
        } else if (hijri?.isDhulHijjah) {
          score += 15; reasons.push('Dhul Hijjah');
        }
        break;

      case 'muharram-tracker':
        if (hijri?.isFirst10Muharram) {
          score += 25; reasons.push('first 10 Muharram (Ashura)');
        } else if (hijri?.isMuharram) {
          score += 8; reasons.push('Muharram');
        }
        break;

      // -- Season & weather boosts ----------------------------------------
      case 'seasonal-food_winter':
        if (season === 'winter') { score += 8; reasons.push('winter season'); }
        break;

      case 'air-card':
        if (typeof ctx.weatherAqi === 'number' && ctx.weatherAqi >= 100) {
          score += 30; reasons.push('poor air quality');
        }
        break;

      case 'weather':
        if (typeof ctx.weatherTemp === 'number' && (ctx.weatherTemp <= 0 || ctx.weatherTemp >= 32)) {
          score += 8; reasons.push('extreme temperature');
        }
        break;

      // -- Country/city change boosts -------------------------------------
      case 'currency-ticker':
        if (countryChanged) { score += 40; reasons.push('country changed — currency hint'); }
        else if (ctx.location.traveling) { score += 12; reasons.push('travel mode'); }
        break;

      case 'translate-box':
        if (countryChanged) { score += 35; reasons.push('country changed — language barrier likely'); }
        else if (ctx.location.traveling) { score += 10; reasons.push('travel mode'); }
        break;

      case 'travel-radar':
        if (ctx.location.traveling) { score += 35; reasons.push('travel mode'); }
        break;

      case 'around-me_local':
        if (cityChanged && !countryChanged) { score += 12; reasons.push('new city, same country'); }
        break;

      // -- Cook (meal hours) ---------------------------------------------
      case 'cook-card':
        if (dayPart === 'midday' || (dayPart === 'evening' && ctx.now.getHours() < 19)) {
          score += 18; reasons.push('meal planning hours');
        }
        if (isWeekend) score += 4;
        break;

      // -- Outdoor windows (boost during daylight; bury at night) --------
      case 'outdoor-windows':
        if (dayPart === 'morning' || dayPart === 'midday' || dayPart === 'afternoon') {
          score += 14; reasons.push('daylight outdoor planning hours');
        }
        if (dayPart === 'evening' && ctx.now.getHours() < 19) {
          score += 8; reasons.push('late afternoon walk hours');
        }
        if (dayPart === 'night' || dayPart === 'lateNight') {
          score -= 12; reasons.push('night — outdoors not the time');
        }
        if (isWeekend) score += 4;
        break;

      // -- Watch (entertainment hours) -----------------------------------
      case 'watch-hub':
        if (dayPart === 'evening' || dayPart === 'night') {
          score += 12; reasons.push('evening watch hours');
        }
        if (isWeekend) score += 6;
        break;

      // -- Shop (impulse hours: lunch/evening, weekends) -----------------
      case 'shop-strip':
        if (dayPart === 'midday' || dayPart === 'afternoon') {
          score += 6; reasons.push('shopping browse hours');
        }
        if (isWeekend) score += 4;
        // Black-friday-ish window (Nov 20 — Dec 1, US calendar) — bump.
        if (ctx.now.getMonth() === 10 && ctx.now.getDate() >= 20) {
          score += 12; reasons.push('Black Friday season');
        }
        break;

      // -- Always-zero default --------------------------------------------
      default:
        break;
    }

    const score_obj: AnticipationScore = {
      widgetId,
      score,
      reason: reasons.length ? reasons.join(' + ') : 'neutral',
    };
    this.scores.set(widgetId, score_obj);
    return score_obj;
  }

  /** Get the most recently computed score (debug / analytics). */
  getLastScore(widgetId: string): AnticipationScore | undefined {
    return this.scores.get(widgetId);
  }
}
